import { memo, useMemo } from 'react'

import { Divider } from 'antd'
import styled from 'styled-components'

import { BlockGroup, Block as BlockType } from '../types/blueprint'

import { AddAnother } from './AddAnother'
import CaptureBlock from './CaptureBlock'
import DisplayBlock from './DisplayBlock'
import { DisplayDescription } from './DisplayBlock.style'
import { shouldRenderBlockOrGroup } from './utils/conditionsHelpers'

import PreventPageBreak from '@/components/shared-components/PrintMedia'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { useIntervalWatch } from '@/hooks/useIntervalWatch'

export const BlockGroupTitle = styled.span`
  ${({ theme }) => theme.font.subheading02}
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
  display: block;
  white-space: pre-wrap;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 0.5rem;
`

type Props = {
  block: BlockType
  index: number
  blockGroup: BlockGroup
  sectionId: string
  isPreview?: boolean
}

/**
 * The parent Block Group will pass it's name and desciption down to the first block
 * to render inside of the `<PreventPageBreak>` component. This ensures that the
 * block group title and description will be rendered on the same page as the first
 * block in the PDF and not orphaned on the previous page.
 */
const Block: React.FC<Props> = memo(
  ({ index, block, blockGroup, sectionId, isPreview }) => {
    const { renderOptions } = useTemplateRenderer()

    const blockHasConditions = useMemo(
      () =>
        block.conditions?.flatMap((group) =>
          group?.map((condition) => condition.blockId)
        )?.length,
      [block.conditions]
    )

    if (renderOptions?.renderingInPdf && block.hideFromPdf) {
      return null
    }

    if (blockHasConditions) {
      return (
        <InternalBlockIfConditions
          index={index}
          block={block}
          blockGroup={blockGroup}
          sectionId={sectionId}
          isPreview={isPreview}
        />
      )
    }

    return (
      <InternalBlockIfNoConditions
        index={index}
        block={block}
        blockGroup={blockGroup}
        sectionId={sectionId}
        isPreview={isPreview}
      />
    )
  }
)

const InternalBlockIfConditions: React.FC<Props> = memo(
  ({ index, block, blockGroup, sectionId, isPreview }) => {
    const { renderOptions, isCapture, mode, form, config } =
      useTemplateRenderer()

    const fieldsToWatch = useMemo(
      () =>
        block.conditions?.flatMap((group) =>
          group?.map((condition) => condition.blockId)
        ) ?? [],
      [block.conditions]
    )
    const watchedValues = useIntervalWatch(fieldsToWatch, form)

    if (
      !isPreview &&
      !shouldRenderBlockOrGroup({
        blockOrGroup: block,
        mode,
        formData: watchedValues,
        isPublic: config?.isPublic,
      })
    ) {
      return null
    }

    return (
      <PreventPageBreak
        shouldAvoidPageBreak={renderOptions.preventPageBreakInsideBlock}
      >
        {index === 0 && blockGroup.name && (
          <BlockGroupTitle>
            {blockGroup.name}
            <Divider style={{ margin: '0.5rem 0rem' }} />

            {isCapture && (
              <AddAnother
                type="BLOCK_GROUP"
                item={blockGroup}
                parentId={sectionId}
              />
            )}
          </BlockGroupTitle>
        )}
        {index === 0 && blockGroup.description && (
          <DisplayDescription>{blockGroup.description}</DisplayDescription>
        )}

        {block.kind === 'display' ? (
          <DisplayBlock block={block} />
        ) : (
          <CaptureBlock block={block} blockGroupId={blockGroup.id} />
        )}
      </PreventPageBreak>
    )
  }
)

const InternalBlockIfNoConditions: React.FC<Props> = memo(
  ({ index, block, blockGroup, sectionId }) => {
    const { renderOptions, isCapture, config } = useTemplateRenderer()

    if (config?.isPublic && block.adminOnlyVisibility) return null
    return (
      <PreventPageBreak
        shouldAvoidPageBreak={renderOptions.preventPageBreakInsideBlock}
      >
        {index === 0 && blockGroup.name && (
          <BlockGroupTitle>
            {blockGroup.name}
            <Divider style={{ margin: '0.5rem 0rem' }} />

            {isCapture && (
              <AddAnother
                type="BLOCK_GROUP"
                item={blockGroup}
                parentId={sectionId}
              />
            )}
          </BlockGroupTitle>
        )}
        {index === 0 && blockGroup.description && (
          <DisplayDescription>{blockGroup.description}</DisplayDescription>
        )}

        {block.kind === 'display' ? (
          <DisplayBlock block={block} />
        ) : (
          <CaptureBlock block={block} blockGroupId={blockGroup.id} />
        )}
      </PreventPageBreak>
    )
  }
)

export default Block
